import { useQuery } from '@tanstack/react-query';
import { OctagonAlert } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { apiFetch } from '@/lib/api';
import { useQuotaModalStore } from '@/stores/quotaModal';

/** v0.3.x (AIトークン利用上限管理、F-10): AI機能呼出しが 429 AI_QUOTA_EXCEEDED で拒否された際に
 *  全画面共通で表示するポップアップ。App.tsx に常設マウントし、main.tsx で lib/api.ts の
 *  registerQuotaExceededHandler 経由で開閉を注入する (registerAuthClearedHandler と同じ方式)。 */
const formatResetAt = (iso: string): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // JST 表示 (テナントタイムゾーンはv0.3.x時点でJST固定、docs/design/v03-ai-token-quota-detail.md §10)
    const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    const y = jst.getUTCFullYear();
    const m = String(jst.getUTCMonth() + 1).padStart(2, '0');
    const day = String(jst.getUTCDate()).padStart(2, '0');
    const hh = String(jst.getUTCHours()).padStart(2, '0');
    const mm = String(jst.getUTCMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm} (JST)`;
  } catch {
    return iso;
  }
};

export function QuotaExceededModal() {
  const isOpen = useQuotaModalStore((s) => s.isOpen);
  const info = useQuotaModalStore((s) => s.info);
  const close = useQuotaModalStore((s) => s.close);

  // Q9対応: admin_contact_email が設定されていればメール連絡のフォールバックを表示する。
  // モーダルが開いている時のみ取得 (常時ポーリングしない)
  const { data: settings } = useQuery<Record<string, unknown>>({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<Record<string, unknown>>('/tenant-settings'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  const adminEmail = typeof settings?.ai_usage_admin_contact_email === 'string' ? settings.ai_usage_admin_contact_email : null;

  if (!info) return null;

  return (
    <Modal
      open={isOpen}
      onClose={close}
      title="AI 機能の利用上限に到達しました"
      role="alertdialog"
      size="sm"
      footer={
        <>
          {adminEmail && (
            <a
              href={`mailto:${adminEmail}?subject=${encodeURIComponent('AI利用上限の引き上げ相談')}`}
              className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken focus-ring min-h-[44px] sm:min-h-[40px] flex items-center"
            >
              メールで管理者に連絡
            </a>
          )}
          <button
            type="button"
            onClick={close}
            autoFocus
            className="h-10 px-5 rounded-md text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover focus-ring min-h-[44px] sm:min-h-[40px] bg-brand-red"
          >
            閉じる
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-text-body">
        <div className="flex items-start gap-2">
          <OctagonAlert size={20} className="text-danger-text shrink-0 mt-0.5" aria-hidden="true" />
          <p>{info.scopeLabel}に達しているため、この AI 機能は現在ご利用いただけません。</p>
        </div>
        <dl className="grid grid-cols-2 gap-x-2 gap-y-1 bg-surface-sunken rounded-md p-3">
          <dt className="text-text-sub">消費</dt>
          <dd className="text-right tabular-nums">${info.spentUsd.toFixed(2)}</dd>
          <dt className="text-text-sub">上限</dt>
          <dd className="text-right tabular-nums">${info.limitUsd.toFixed(2)}</dd>
          <dt className="text-text-sub">リセット予定</dt>
          <dd className="text-right tabular-nums">{formatResetAt(info.resetAt)}</dd>
        </dl>
        <p className="text-xs text-text-sub">上限の引き上げについては管理者にご相談ください。</p>
      </div>
    </Modal>
  );
}
